import { AccessGate, useCan } from '../../shared/access/AccessControls';
import React, { useEffect, useMemo, useState } from 'react';
import Wrapper from '../../shared/components/Wrapper';
import { Button, Divider, Text } from '@chakra-ui/react';
import { Company } from '../../shared/types/company';
import { integrationDefinitionsApi } from '../../store/services/IntegrationDefinitionsService';
import IntegrationBindingRow from './components/AttachedRoleRow';
import { StyledRolesWrapper } from '../profiles/styled';
import { companiesApi } from '../../store/services/CompaniesService';

const CompanyIntegrationBindingsEditor = ({ company }: { company: Company }) => {
  const canRead = useCan('ADMIN_ATTACHED_ROLE_READ');
  const canListDefinitions = useCan('ADMIN_ROLE_LIST');
  const roles = useMemo(() => company?.attachedRoles ?? [], [company]);
  const { currentData, isFetching, isError, refetch } =
    companiesApi.useGetCompanyAttachedRolesQuery(
      { companyId: company?.id ?? '' },
      { skip: !canRead || !company?.id, refetchOnMountOrArgChange: true },
    );
  // Never fall back to attributes in the company response, including during
  // rollout against the older API. currentData also excludes a previous company.
  const configurations =
    !isFetching &&
    !isError &&
    currentData &&
    Array.isArray(currentData.attachedRoles)
      ? currentData.attachedRoles
      : [];

  const [trigger] = integrationDefinitionsApi.useLazyGetRolesQuery();

  useEffect(() => {
    if (canListDefinitions) trigger({});
  }, [trigger, canListDefinitions]);

  return (
    <Wrapper>
      {(!company && <p>Loading company info...</p>) || (
        <>
          <Text mb={'1rem'}>
            <b>Company integration bindings</b>
          </Text>

          {isFetching && (
            <Text role="status">Loading integration configuration...</Text>
          )}
          {isError && (
            <Text role="alert">
              Could not load integration configuration. Saving is disabled.
            </Text>
          )}
          <Button
            size="sm"
            mb="1rem"
            isDisabled={isFetching}
            onClick={() => refetch()}
          >
            Reload configuration
          </Button>

          <StyledRolesWrapper>
            {(!!roles.length &&
              roles.map((role) => (
                <React.Fragment key={role.id}>
                  <IntegrationBindingRow
                    companyId={company.id}
                    role={role}
                    configuration={configurations.find(
                      (entry) => entry.id === role.id,
                    )}
                  />
                  <Divider />
                </React.Fragment>
              ))) || (
              <>
                <p>No integration bindings</p>
                <Divider />
              </>
            )}

            <IntegrationBindingRow companyId={company.id} />
          </StyledRolesWrapper>
        </>
      )}
    </Wrapper>
  );
};

const CompanyIntegrationBindings = ({ company }: { company: Company }) => {
  const [editing, setEditing] = useState(false);
  return <Wrapper><>
    <Text as="h2">Integration bindings</Text>
    {!editing && (company?.attachedRoles || []).map(binding => <div key={binding.id}><b>{binding.role.name}</b><p>{binding.role.description}</p></div>)}
    <AccessGate action="ADMIN_ATTACHED_ROLE_READ">
      <Button onClick={() => setEditing(value => !value)}>{editing ? 'Close integration editor' : 'Edit integration bindings'}</Button>
      {editing && <CompanyIntegrationBindingsEditor company={company} />}
    </AccessGate>
  </></Wrapper>;
};
export default CompanyIntegrationBindings;
